import { useRef, useState } from "react";
import { uploadSync } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";
import { UploadCloud, FileText, Loader2 } from "lucide-react";

export default function FileUploader({ projectId, onUploaded }) {
  const { t } = useI18n();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [name, setName] = useState("");
  const [isBaseline, setIsBaseline] = useState(false);
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const onChoose = (f) => {
    if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!["xml", "csv", "mpp"].includes(ext)) {
      toast.error(`Formato não suportado: .${ext}. Aceitos: .xml, .csv, .mpp`);
      return;
    }
    setFile(f);
    if (!name) setName(f.name.replace(/\.[^/.]+$/, ""));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Selecione um arquivo"); return; }
    const fd = new FormData();
    fd.append("file", file);
    if (name) fd.append("name", name);
    fd.append("is_baseline", String(isBaseline));
    if (notes) fd.append("notes", notes);
    setUploading(true); setProgress(0);
    try {
      const data = await uploadSync(projectId, fd, {
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      if (data?.warnings?.length) {
        data.warnings.forEach((w) => toast.warning(w));
      } else {
        toast.success(`${data.task_count} tarefas importadas`);
      }
      setFile(null); setName(""); setNotes(""); setIsBaseline(false);
      if (inputRef.current) inputRef.current.value = "";
      onUploaded?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setUploading(false); setProgress(0); }
  };

  return (
    <form onSubmit={onSubmit} className="bg-white border border-zinc-200 rounded-md p-5 space-y-4" data-testid="file-uploader">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); onChoose(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-md p-8 text-center cursor-pointer transition-colors ${dragging ? "border-cs-accent bg-orange-50" : "border-zinc-300 hover:border-zinc-400"}`}
        data-testid="uploader-dropzone"
      >
        {file ? (
          <div className="flex items-center justify-center gap-2 text-sm text-zinc-700">
            <FileText size={18} className="text-cs-accent" />
            <span className="font-mono">{file.name}</span>
            <span className="font-mono text-xs text-zinc-500">({(file.size / 1024).toFixed(1)} KB)</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-zinc-500">
            <UploadCloud size={24} />
            <p className="text-sm">{t("drop_files")}</p>
          </div>
        )}
        <input ref={inputRef} type="file" accept=".xml,.csv,.mpp" className="hidden" onChange={(e) => onChoose(e.target.files?.[0])} data-testid="uploader-input" />
      </div>

      {file && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="cs-label block mb-1.5">{t("sync_name")}</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" data-testid="uploader-name-input" />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-zinc-700">
              <input type="checkbox" checked={isBaseline} onChange={(e) => setIsBaseline(e.target.checked)} className="rounded text-cs-accent focus:ring-cs-accent" data-testid="uploader-baseline-checkbox" />
              {t("set_baseline")}
            </label>
          </div>
          <div className="md:col-span-2">
            <label className="cs-label block mb-1.5">{t("notes")}</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" data-testid="uploader-notes-input" />
          </div>
        </div>
      )}

      {uploading && progress > 0 && (
        <div className="h-1 bg-zinc-100 rounded-full overflow-hidden">
          <div className="h-full bg-cs-accent transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <button type="submit" disabled={uploading || !file} className="inline-flex items-center gap-2 bg-cs-accent text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-cs-accentHover disabled:opacity-60" data-testid="uploader-submit-btn">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} {uploading ? t("generating") : t("upload_schedule")}
        </button>
      </div>
    </form>
  );
}
